import { useState, useEffect, useCallback } from 'react'
import { Prompt, PassId, PromptSlot } from '../types'
import { DEFAULT_PROMPTS } from '../data/defaultPrompts'
import { supabase } from '../supabaseClient'
import { useAuth } from '../providers/AuthProvider'

/**
 * Manages prompts in Supabase `prompts` table.
 * On first load, seeds defaults if the table is empty for this user.
 * Version history uses parent_id chain + is_active flag.
 */
export function usePrompts() {
  const { user } = useAuth()
  const [prompts, setPrompts] = useState<Prompt[]>([])
  const [loading, setLoading] = useState(true)
  const [seeded, setSeeded] = useState(false)

  // ── Load active prompts ────────────────────────────────────────────────────
  const load = useCallback(async () => {
    const { data, error } = await supabase
      .from('prompts')
      .select('*')
      .eq('is_active', true)
      .order('pass_id')
      .order('slot')

    if (error) {
      console.error('usePrompts load error:', error)
      setLoading(false)
      return
    }

    setPrompts((data ?? []) as Prompt[])
    setLoading(false)
  }, [])

  // ── Seed defaults if table is empty ────────────────────────────────────────
  useEffect(() => {
    if (!user || seeded) return

    async function seedIfEmpty() {
      // Check if any prompts exist at all
      const { count, error } = await supabase.from('prompts').select('id', { count: 'exact', head: true })

      if (error) {
        console.error('usePrompts seed check error:', error)
        await load()
        setSeeded(true)
        return
      }

      if (count === 0) {
        // Seed defaults
        const rows = DEFAULT_PROMPTS.map((def) => ({
          pass_id: def.pass_id,
          slot: def.slot,
          label: def.label,
          role_description: def.role_description,
          text: def.text,
          is_active: true,
          version: 1,
          parent_id: null,
          created_by: user!.id,
        }))

        const { error: insertErr } = await supabase.from('prompts').insert(rows)
        if (insertErr) console.error('usePrompts seed error:', insertErr)
      }

      await load()
      setSeeded(true)
    }

    seedIfEmpty()
  }, [user, seeded, load])

  // ── Get active prompt for a pass/slot, optionally scoped by method ─────────
  // If methodId is omitted, returns any active prompt for that pass/slot
  // (falls back for backward-compat with code that doesn't know about methods).
  const getPrompt = useCallback(
    (passId: PassId, slot: PromptSlot, methodId?: string): Prompt | undefined => {
      if (methodId) {
        return prompts.find((p) => p.pass_id === passId && p.slot === slot && p.method_id === methodId)
      }
      return prompts.find((p) => p.pass_id === passId && p.slot === slot)
    },
    [prompts],
  )

  // ── Delete a specific prompt row (a version). Refuses to delete the
  // currently-active prompt — activate a different version first.
  // Caller should confirm; this is destructive.
  const deletePromptVersion = useCallback(
    async (id: string): Promise<{ ok: boolean; error?: string }> => {
      const target = prompts.find((p) => p.id === id)
      // If target is the active prompt, caller is responsible for
      // activating a different version first. We still permit the
      // delete in case the call is from the version history panel where
      // the activation guard isn't available — but warn here.
      if (target?.is_active) {
        return {
          ok: false,
          error: 'Cannot delete the active version. Activate a different version first, then delete this one.',
        }
      }
      // Best-effort: rewire any children whose parent_id points here
      // so the version chain isn't broken. They'll point at this row's
      // parent (could be null for the root).
      if (target?.parent_id !== undefined) {
        await supabase
          .from('prompts')
          .update({ parent_id: target?.parent_id ?? null })
          .eq('parent_id', id)
      }
      const { error } = await supabase.from('prompts').delete().eq('id', id)
      if (error) {
        console.error('usePrompts delete error:', error)
        return { ok: false, error: error.message }
      }
      await load()
      return { ok: true }
    },
    [prompts, load],
  )

  // ── Activate a specific version (deactivate others in the same pass/slot/method)
  // Fetches the target row from the DB — `prompts` state only holds active rows,
  // so a local lookup would return undefined for every non-active version.
  const activateVersion = useCallback(
    async (id: string): Promise<{ ok: boolean; error?: string }> => {
      const { data: target, error: fetchErr } = await supabase
        .from('prompts')
        .select('id, pass_id, slot, method_id, is_active')
        .eq('id', id)
        .single()
      if (fetchErr || !target) {
        console.error('activateVersion fetch error:', fetchErr)
        return { ok: false, error: fetchErr?.message ?? 'Prompt not found' }
      }
      if (target.is_active) {
        await load()
        return { ok: true }
      }
      let deactivate = supabase
        .from('prompts')
        .update({ is_active: false })
        .eq('pass_id', target.pass_id)
        .eq('slot', target.slot)
      if (target.method_id) deactivate = deactivate.eq('method_id', target.method_id)
      const { error: deErr } = await deactivate
      if (deErr) {
        console.error('activateVersion deactivate error:', deErr)
        return { ok: false, error: deErr.message }
      }
      const { error: acErr } = await supabase.from('prompts').update({ is_active: true }).eq('id', id)
      if (acErr) {
        console.error('activateVersion activate error:', acErr)
        return { ok: false, error: acErr.message }
      }
      await load()
      return { ok: true }
    },
    [load],
  )

  // ── Update prompt in place (fine-tuning — no new version row) ─────────
  // Use when iterating on a prompt to dial in good results. Version history
  // stays stable; only the current row's text/label change.
  const updatePromptInPlace = useCallback(
    async (id: string, text: string, label: string): Promise<void> => {
      const { error } = await supabase.from('prompts').update({ text, label }).eq('id', id)
      if (error) {
        console.error('usePrompts update error:', error)
        return
      }
      await load()
    },
    [load],
  )

  // ── Save new version (deactivate old, insert new, same method) ────────────
  const savePrompt = useCallback(
    async (id: string, text: string, label: string): Promise<void> => {
      const current = prompts.find((p) => p.id === id)
      if (!current) return

      // Deactivate old version
      await supabase.from('prompts').update({ is_active: false }).eq('id', id)

      // Insert new version with parent_id linking to old
      const { error } = await supabase.from('prompts').insert({
        pass_id: current.pass_id,
        slot: current.slot,
        label,
        role_description: current.role_description,
        text,
        is_active: true,
        version: current.version + 1,
        parent_id: id,
        method_id: current.method_id,
        created_by: user?.id ?? null,
      })

      if (error) {
        console.error('usePrompts save error:', error)
        // Re-activate old version on failure
        await supabase.from('prompts').update({ is_active: true }).eq('id', id)
        return
      }

      await load()
    },
    [prompts, user, load],
  )

  // ── Create a brand-new prompt for a method (no parent) ─────────────────────
  const createPrompt = useCallback(
    async (input: {
      passId: PassId
      slot: PromptSlot
      methodId: string
      label: string
      roleDescription: string
      text: string
    }): Promise<Prompt | null> => {
      const { data, error } = await supabase
        .from('prompts')
        .insert({
          pass_id: input.passId,
          slot: input.slot,
          label: input.label,
          role_description: input.roleDescription,
          text: input.text,
          is_active: true,
          version: 1,
          parent_id: null,
          method_id: input.methodId,
          created_by: user?.id ?? null,
        })
        .select()
        .single()
      if (error) {
        console.error('createPrompt:', error.message)
        return null
      }
      await load()
      return data as Prompt
    },
    [user, load],
  )

  // ── Fetch version history (chain of versions) ──────────────────────────────
  const fetchVersionHistory = useCallback(
    async (passId: PassId, slot: PromptSlot, methodId?: string): Promise<Prompt[]> => {
      let query = supabase.from('prompts').select('*').eq('pass_id', passId).eq('slot', slot)
      if (methodId) query = query.eq('method_id', methodId)
      const { data, error } = await query.order('version', { ascending: false })

      if (error) {
        console.error('usePrompts history error:', error)
        return []
      }

      return (data ?? []) as Prompt[]
    },
    [],
  )

  // ── Reload ────────────────────────────────────────────────────────────────
  const reload = useCallback(() => {
    load()
  }, [load])

  return {
    prompts,
    getPrompt,
    savePrompt,
    updatePromptInPlace,
    createPrompt,
    deletePromptVersion,
    activateVersion,
    fetchVersionHistory,
    loading,
    reload,
  }
}
